<script setup lang="ts" name="BaseDatePicker">
import { computed, reactive, ref, watch } from 'vue';
import { useScreens } from 'vue-screen-utils';
import { format, getMonth, getYear, isValid, parse } from 'date-fns';

import DatePickerField from '~/components/DatePickerField.vue';

import type { IDatePickerDisabledDates, IPeriodNew } from '~/types';

const props = defineProps<{
  modelValue: IPeriodNew;
  minDate: Date | null;
  maxDate: Date | null;
  labelTo?: string;
  labelFrom: string;
  range?: boolean;
  start?: Date;
  end?: Date;
  disabledDates?: IDatePickerDisabledDates[];
}>();

const emit = defineEmits<{
  'update:modelValue': [value: IPeriodNew];
}>();

const DATE_FORMAT = 'dd/MM/yyyy';

const calendar = ref();

const { mapCurrent } = useScreens({
  xs: '0px',
  sm: '640px',
  md: '768px',
  lg: '1024px',
});

const columns = mapCurrent({ lg: 2 }, 2);

const state = reactive({
  date: {
    start: props.modelValue.cp_start,
    end: props.modelValue.cp_end,
  },
});

const formattedDate = computed(() => ({
  start: state.date.start
    ? format(new Date(state.date.start), DATE_FORMAT)
    : null,
  end: state.date.end ? format(new Date(state.date.end), DATE_FORMAT) : null,
}));

const updateModelValue = () => {
  const period: IPeriodNew = {
    ...props.modelValue,
    cp_start: state.date.start,
    cp_end: state.date.end,
  };

  emit('update:modelValue', period);
};

const onDrag = (event) => {
  state.date.start = event.start;
  state.date.end = event.end;

  updateModelValue();
};

const onDayClick = () => {
  updateModelValue();
};

const parseAndValidate = (value: string): Date | null => {
  const parsedDate = parse(value, DATE_FORMAT, new Date());
  return isValid(parsedDate) ? parsedDate : null;
};

const isValidDate = (date: Date | null): boolean => {
  if (!date) return false;

  const withinMinDate = props.minDate ? date >= props.minDate : true;
  const withinMaxDate = props.maxDate ? date <= props.maxDate : true;

  return withinMinDate && withinMaxDate && !isDateDisabled(date);
};

const isDateDisabled = (date: Date | null): boolean => {
  if (!date || !props.disabledDates) return false;

  return props.disabledDates.some((disabledDate) => {
    if (!disabledDate.repeat) return false;

    if (disabledDate.repeat.on) {
      const dateInfo = { date, day: date.getDate() };

      return disabledDate.repeat.on.some((onFunction) => onFunction(dateInfo));
    }

    return false;
  });
};

const onInputBlur = (value: string, type: 'start' | 'end') => {
  const date = parseAndValidate(value);

  if (isValidDate(date)) {
    state.date[type] = date;
  } else {
    state.date[type] = null;
  }

  const validStart =
    typeof state.date.start === 'string'
      ? isValidDate(new Date(state.date.start))
      : isValidDate(state.date.start);

  const validEnd =
    typeof state.date.end === 'string'
      ? isValidDate(new Date(state.date.end))
      : isValidDate(state.date.end);

  if (props.range && validEnd && validStart) {
    calendar.value.updateValue(state.date);
  }

  updateModelValue();

  if (date && shouldMoveToDate(date)) {
    calendar.value.move(date);
  }
};

const datesAreTheSame = (
  newDate: Date | string | null,
  currentDate: Date | string | null,
) => newDate === currentDate && newDate !== null;

const isDateWithinCalendarPages = (date: Date, month: number, year: number) =>
  getMonth(date) + 1 === month && getYear(date) === year;

const shouldMoveToDate = (newDate: Date | string) => {
  const date = new Date(newDate);

  return !(
    isDateWithinCalendarPages(
      date,
      calendar.value.calendarRef.firstPage.month,
      calendar.value.calendarRef.firstPage.year,
    ) ||
    isDateWithinCalendarPages(
      date,
      calendar.value.calendarRef.lastPage.month,
      calendar.value.calendarRef.lastPage.year,
    )
  );
};

watch(
  () => props.modelValue,
  (value) => {
    if (
      datesAreTheSame(value.cp_start, state.date.start) ||
      datesAreTheSame(value.cp_end, state.date.end)
    ) {
      return;
    }

    state.date.start = value.cp_start;
    state.date.end = value.cp_end;

    if (!value.cp_start) return;

    if (calendar.value && shouldMoveToDate(value.cp_start)) {
      calendar.value.move(value.cp_start);
    }
  },
  {
    deep: true,
  },
);
</script>

<template>
  <div class="my-4 max-w-fit space-y-4">
    <div class="grid grid-cols-2 gap-x-1 px-4 md:gap-x-6">
      <DatePickerField
        name="from"
        :modelValue="formattedDate.start"
        :label="labelFrom"
        :value="formattedDate.start"
        @on-blur="(value) => onInputBlur(value, 'start')"
      />

      <div class="mb-3 mt-auto w-4 border-t border-charcoal-6 md:hidden" />

      <DatePickerField
        v-if="range"
        name="to"
        :label="labelTo"
        :modelValue="formattedDate.end"
        :value="formattedDate.end"
        @on-blur="(value) => onInputBlur(value, 'end')"
      />
    </div>

    <VDatePicker
      v-if="range"
      v-model.range="state.date"
      ref="calendar"
      mode="date"
      color="orange"
      borderless
      :is-range="range"
      :min-date="minDate"
      :max-date="maxDate"
      :disabledDates="disabledDates"
      :columns="columns"
      @drag="onDrag"
    />

    <VDatePicker
      v-else
      v-model="state.date.start"
      ref="calendar"
      mode="date"
      color="orange"
      borderless
      :is-range="range"
      :min-date="minDate"
      :max-date="maxDate"
      :disabledDates="disabledDates"
      :columns="columns"
      @dayclick="onDayClick"
    />
  </div>
</template>

<style>
.vc-light.vc-attr,
.vc-light .vc-attr {
  --vc-rounded-full: 3px !important;
  --vc-focus-ring: theme('colors.ocean.3') !important;
  --vc-day-content-disabled-color: theme('colors.charcoal.4') !important;
  --vc-highlight-solid-bg: theme('colors.secondary.3') !important;
}

.vc-highlight-content-solid,
.vc-highlight-content-light,
.vc-highlight-content-outline,
.vc-day-content {
  font-weight: normal !important;
}

.vc-weeks > .vc-weekdays {
  padding-top: 16px;
}

.vc-weeks > .vc-weekdays > .vc-weekday {
  font-weight: bold;
  color: theme('colors.charcoal.8');
}

.vc-week .vc-day .vc-day-content {
  padding: 20px;
}

.vc-highlight {
  width: 40px !important;
  height: 40px !important;
}

.vc-disabled {
  color: theme('colors.charcoal.4') !important;
}

.vc-day-content:not(.vc-disabled) {
  color: theme('colors.secondary.9');
}

.vc-highlight-content-solid {
  color: theme('colors.secondary.9');
}
</style>
